import React, {useEffect} from "react";
import { styled } from "@mui/material/styles";

import { Box } from "../../UI/Box";
import { List } from "../../UI/List";
import { Paper } from "../../UI/Paper";
import { ListItemButton } from "../../UI/ListItemButton";

import { useDebounce } from "../../hooks/useDebounce";
import { normalize } from "../../helpers/normalize";
import useAuth from "../../routes/login/AuthContext";
import { Progress } from "../Progress";
import { TextField } from "../TextField";
import { IdNameTransport } from "../../services/Common";
import { SearchNetworkByName } from "../../services/NetworkApi";
import { useNetwork } from "../../hooks/useNetwork";
import { useSelectedNetworkContext } from "../../contexts/NetworkContext/context";
import { Grid } from "@mui/material";

const StyledListWrapper = styled(Paper)`
  width: 100%;
  max-height: 300px;
  overflow-y: auto;
  position: absolute;

  & .MuiListItemButton-root {
    font-size: 14px;
  }
`;

export const NetworkSearchInput = () => {
  const { getAuthToken } = useAuth();
  const { setSelectedNetworkId } = useSelectedNetworkContext();
  const { selectedNetwork, selecteNetworkAsync } = useNetwork();
  const [networks, setNetworks] = React.useState<IdNameTransport[]>([]);
  const [searchValue, setSearchValue] = React.useState("");
  const debouncedValue = useDebounce(searchValue, 500);
  const [isLoading, setLoading] = React.useState(false);

  const setNetworkfromLS = async () => {
    try {
      const networkId = Number(localStorage.getItem("selectedNetworkId"));
      await selecteNetworkAsync(networkId);
    } catch (e) {
      setSelectedNetworkId(0);
    }
  };

  const setDefaultNetwork = () => setSelectedNetworkId(0);

  useEffect(() => {
    setNetworkfromLS();
  },[]);

  const handleKeyDown: React.KeyboardEventHandler<HTMLInputElement> = (e) => {
    if (e.keyCode === 8 && selectedNetwork.l1Id !== 0) {
      setDefaultNetwork();
    }
  };

  React.useEffect(() => {
    if ((debouncedValue.length < 3 && isNaN(Number(debouncedValue))) || debouncedValue === "") {
      setNetworks([]);
      return;
    }

    const getNetworks = async () => {
      setLoading(true);

      const results = await SearchNetworkByName(
        debouncedValue,
        await getAuthToken()
      );

      if (results) {
        setNetworks(results);
      }

      setLoading(false);
    };

    getNetworks();
  }, [normalize(debouncedValue)]);

  const isNetworksShow = React.useMemo(() => !!networks.length, [
    normalize(networks),
  ]);

  const value = React.useMemo(() => {
    return selectedNetwork.l1Id === 0
      ? searchValue
      : `${selectedNetwork.l1Name} - ${selectedNetwork.l1Id}`;
  }, [searchValue, normalize(selectedNetwork)]);

  const portfolioValue = React.useMemo(() => {
    return selectedNetwork.l1Id === 0
      ? ''
      : `${selectedNetwork.portfolio?.name || ''}`;
  }, [searchValue, normalize(selectedNetwork)]);

  const handleNetworkSelect = (id: number) => async () => {
    await selecteNetworkAsync(id);
    localStorage.setItem("selectedNetworkId", id.toString());
    setSearchValue("");
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (selectedNetwork.l1Id !== 0) {
      return;
    }

    setSearchValue(e.target.value);
  };

  const handleFocus = () => {
    if (selectedNetwork.l1Id === 0) {
      return;
    }

    setDefaultNetwork();
  };

  return (
    <Box position="relative">
      <TextField
        value={value}
        onFocus={handleFocus}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        placeholder="Type in at least 3 characters to search"
        elemAfterInput={<Grid mr={1.5}>{portfolioValue}</Grid>}
      />
      {isLoading && (
        <Box position="absolute" bottom={-2} height={8} width="100%">
          <Progress type="linear" isIndeterminate />
        </Box>
      )}
      {isNetworksShow && (
        <StyledListWrapper>
          <List>
            {networks.map((network) => (
              <ListItemButton
                key={network.id}
                onClick={handleNetworkSelect(network.id)}
              >
                <Grid container>
                  <Grid item xs>
                    {network.name} - {network.id}
                  </Grid>
                  <Grid item>
                    {network.portfolio?.portfolioName}
                  </Grid>
                </Grid>
                 
              </ListItemButton>
            ))}
          </List>
        </StyledListWrapper>
      )}
    </Box>
  );
};
